import React, { useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { SERVICES } from "@/lib/constants";
import { Field } from "@/pages/Leads";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Plus, Building2, Clock, MapPin, Pencil, Trash2 } from "lucide-react";
import { WEEKDAYS } from "@/components/crm/Availability";

const HOURS_HELP = "Sets the booking slots for 'Any doctor', and the after-hours login rules: outside these hours, or on a closed day, login codes go to the super admin and staff are signed out at closing time.";

// "9:00 AM – 8:00 PM · Closed Sun" — closed_days in week order; [] = open daily.
function branchHoursSummary(b) {
  const hours = (b.working_hours || "").replace(/\s*-\s*/, " – ");
  const closed = WEEKDAYS.filter((w) => (b.closed_days || []).includes(w.key)).map((w) => w.label.slice(0, 3));
  return [hours || "Hours not set", closed.length ? `Closed ${closed.join(", ")}` : "Open every day"].join(" · ");
}

// Seven day toggles, Mon..Sun; a pressed day is CLOSED. Stored as closed_days.
function ClosedDaysPicker({ value = [], onChange, testIdPrefix }) {
  const toggle = (key) => onChange(value.includes(key) ? value.filter((d) => d !== key) : [...value, key]);
  return (
    <div className="flex flex-wrap gap-1">
      {WEEKDAYS.map((w) => {
        const closed = value.includes(w.key);
        return (
          <button key={w.key} type="button" onClick={() => toggle(w.key)} aria-pressed={closed} title={closed ? `Closed ${w.label}` : `Open ${w.label}`}
            className={`h-9 w-10 rounded-md border text-xs font-medium transition-colors ${closed ? "border-navy bg-navy text-white" : "border-input bg-white text-ink hover:bg-nude/30"}`}
            data-testid={`${testIdPrefix}-${w.key}`}>
            {w.label.slice(0, 3)}
          </button>
        );
      })}
    </div>
  );
}

const ALL_CLOSED = "A branch can't be closed every day — leave at least one day open.";

export default function Branches() {
  const [items, setItems] = useState([]);
  const [franchises, setFranchises] = useState([]);
  const [managers, setManagers] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = useCallback(() => {
    api.get("/branches").then((r) => setItems(r.data)).catch(() => {});
  }, []);
  useEffect(() => {
    load();
    api.get("/franchises").then((r) => setFranchises(r.data)).catch(() => {});
    api.get("/users", { params: { role: "branch_manager" } }).then((r) => setManagers(r.data)).catch(() => {});
  }, [load]);

  const deleteBranch = async (b) => {
    if (!window.confirm(`Delete branch "${b.name}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/branches/${b.id}`);
      toast.success("Branch deleted");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-5">
      <div className="flex justify-end">
        <Button onClick={() => setOpen(true)} className="bg-navy text-white hover:bg-navy-hover" data-testid="new-branch-btn"><Plus className="mr-2 h-4 w-4" /> Add Branch</Button>
      </div>
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {items.map((b) => (
          <div key={b.id} className="rounded-xl border border-navy/10 bg-white p-5 shadow-sm" data-testid={`branch-${b.id}`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="flex h-11 w-11 items-center justify-center rounded-lg bg-navy/10 text-navy"><Building2 className="h-5 w-5" /></div>
                <div>
                  <p className="font-display font-semibold text-navy">
                    {b.name}
                    {b.code && <span className="ml-2 rounded bg-navy/10 px-1.5 py-0.5 font-mono text-[10px] font-semibold text-navy">{b.code}</span>}
                  </p>
                  <p className="text-xs text-muted-foreground">{b.city}</p>
                </div>
              </div>
              <div className="flex items-center gap-1">
                <button onClick={() => setEditing(b)} className="rounded p-1.5 text-navy/60 hover:bg-navy/10" data-testid={`edit-branch-${b.id}`}><Pencil className="h-3.5 w-3.5" /></button>
                <button onClick={() => deleteBranch(b)} className="rounded p-1.5 text-red-500 hover:bg-red-50" data-testid={`delete-branch-${b.id}`}><Trash2 className="h-3.5 w-3.5" /></button>
              </div>
            </div>
            <div className="mt-4 space-y-2 text-sm text-muted-foreground">
              <p className="flex items-center gap-2"><MapPin className="h-3.5 w-3.5" /> {b.address || "—"}</p>
              <p className="flex items-center gap-2" data-testid={`branch-hours-${b.id}`}>
                <Clock className="h-3.5 w-3.5 shrink-0" /> {branchHoursSummary(b)}
                {b.working_hours && !b.open_time && <span className="text-xs text-amber-700" title="Booking and login use 9 AM–6 PM until this is fixed">(hours unreadable)</span>}
              </p>
            </div>
            <div className="mt-3 flex flex-wrap gap-1.5">
              {(b.services || []).slice(0, 3).map((s) => <span key={s} className="rounded-full bg-nude px-2 py-0.5 text-xs text-navy">{s}</span>)}
            </div>
          </div>
        ))}
      </div>
      <CreateBranch open={open} onOpenChange={setOpen} franchises={franchises} managers={managers} onDone={load} />
      <EditBranch branch={editing} onClose={() => setEditing(null)} franchises={franchises} managers={managers} onDone={load} />
    </div>
  );
}

function CreateBranch({ open, onOpenChange, franchises, managers, onDone }) {
  const empty = { name: "", code: "", city: "", address: "", phone: "", working_hours: "9:00 AM - 8:00 PM", closed_days: ["sun"], franchise_id: "", manager_id: "", services: [] };
  const [form, setForm] = useState(empty);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const submit = async () => {
    if (!form.name) { toast.error("Branch name required"); return; }
    if (form.closed_days.length >= WEEKDAYS.length) { toast.error(ALL_CLOSED); return; }
    try {
      await api.post("/branches", { ...form,
        franchise_id: form.franchise_id || null, manager_id: form.manager_id || null });
      toast.success("Branch created"); setForm(empty); onOpenChange(false); onDone();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader><DialogTitle className="font-display text-navy">Add Branch</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-4 py-2">
          <Field label="Name *"><Input value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="branch-name" /></Field>
          <Field label="Code">
            <Input value={form.code} onChange={(e) => set("code", e.target.value.toUpperCase())} placeholder="MUL" maxLength={5} data-testid="branch-code" />
            <p className="mt-1 text-xs text-muted-foreground">2-5 letters. Used in patient IDs: DMX-{form.code || "MUL"}-00123</p>
          </Field>
          <Field label="City"><Input value={form.city} onChange={(e) => set("city", e.target.value)} /></Field>
          <Field label="Address"><Input value={form.address} onChange={(e) => set("address", e.target.value)} /></Field>
          <Field label="Phone"><Input value={form.phone} onChange={(e) => set("phone", e.target.value)} /></Field>
          <Field label="Working Hours"><Input value={form.working_hours} onChange={(e) => set("working_hours", e.target.value)} placeholder="9:00 AM - 8:00 PM" data-testid="branch-working-hours" /></Field>
          <div className="col-span-2">
            <Field label="Closed on">
              <ClosedDaysPicker value={form.closed_days} onChange={(v) => set("closed_days", v)} testIdPrefix="branch-closed" />
            </Field>
          </div>
          <p className="col-span-2 -mt-2 text-xs text-muted-foreground">{HOURS_HELP}</p>
          <Field label="Franchise">
            <Select value={form.franchise_id} onValueChange={(v) => set("franchise_id", v)}><SelectTrigger><SelectValue placeholder="None" /></SelectTrigger>
              <SelectContent>{franchises.map((f) => <SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>)}</SelectContent></Select>
          </Field>
          <Field label="Manager">
            <Select value={form.manager_id} onValueChange={(v) => set("manager_id", v)}><SelectTrigger><SelectValue placeholder="None" /></SelectTrigger>
              <SelectContent>{managers.map((m) => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}</SelectContent></Select>
          </Field>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} className="bg-navy text-white hover:bg-navy-hover" data-testid="save-branch-btn">Create</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function EditBranch({ branch, onClose, franchises, managers, onDone }) {
  const [form, setForm] = useState(null);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  useEffect(() => {
    if (branch) {
      setForm({
        name: branch.name || "",
        code: branch.code || "",
        city: branch.city || "",
        address: branch.address || "",
        phone: branch.phone || "",
        working_hours: branch.working_hours || "9:00 AM - 8:00 PM",
        closed_days: Array.isArray(branch.closed_days) ? branch.closed_days : ["sun"],
        franchise_id: branch.franchise_id || "",
        manager_id: branch.manager_id || "",
        services: branch.services || [],
      });
    }
  }, [branch]);
  if (!branch || !form) return null;
  const submit = async () => {
    if (!form.name) { toast.error("Branch name required"); return; }
    if (form.closed_days.length >= WEEKDAYS.length) { toast.error(ALL_CLOSED); return; }
    try {
      await api.put(`/branches/${branch.id}`, {
        ...form,
        franchise_id: form.franchise_id || null,
        manager_id: form.manager_id || null,
      });
      toast.success("Branch updated"); onClose(); onDone();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <Dialog open={!!branch} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader><DialogTitle className="font-display text-navy">Edit Branch</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-4 py-2">
          <Field label="Name *"><Input value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="edit-branch-name" /></Field>
          <Field label="Code">
            <Input value={form.code} onChange={(e) => set("code", e.target.value.toUpperCase())} placeholder="MUL" maxLength={5} data-testid="edit-branch-code" />
            <p className="mt-1 text-xs text-muted-foreground">Locked once patients here have IDs.</p>
          </Field>
          <Field label="City"><Input value={form.city} onChange={(e) => set("city", e.target.value)} /></Field>
          <Field label="Address"><Input value={form.address} onChange={(e) => set("address", e.target.value)} /></Field>
          <Field label="Phone"><Input value={form.phone} onChange={(e) => set("phone", e.target.value)} /></Field>
          <Field label="Working Hours"><Input value={form.working_hours} onChange={(e) => set("working_hours", e.target.value)} placeholder="9:00 AM - 8:00 PM" data-testid="edit-branch-working-hours" /></Field>
          <div className="col-span-2">
            <Field label="Closed on">
              <ClosedDaysPicker value={form.closed_days} onChange={(v) => set("closed_days", v)} testIdPrefix="edit-branch-closed" />
            </Field>
          </div>
          <p className="col-span-2 -mt-2 text-xs text-muted-foreground">{HOURS_HELP}</p>
          <Field label="Franchise">
            <Select value={form.franchise_id} onValueChange={(v) => set("franchise_id", v)}><SelectTrigger><SelectValue placeholder="None" /></SelectTrigger>
              <SelectContent>{franchises.map((f) => <SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>)}</SelectContent></Select>
          </Field>
          <Field label="Manager">
            <Select value={form.manager_id} onValueChange={(v) => set("manager_id", v)}><SelectTrigger><SelectValue placeholder="None" /></SelectTrigger>
              <SelectContent>{managers.map((m) => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}</SelectContent></Select>
          </Field>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} className="bg-navy text-white hover:bg-navy-hover" data-testid="update-branch-btn">Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
